import React, { Context, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUserState } from './user-state.provider.ts';
import { fetchViewingHistory } from '../services/library.service';
import type { VideoCardData } from '../types/video-card.types';
import {
    buildVideoWatchStateLookup,
    getVideoWatchStateForCard,
    getVideoWatchStateForIdentity,
    type VideoWatchState,
    type VideoWatchStateLookup
} from '../utils/watch-state';

interface WatchStateContextType {
    watchStateLookup: VideoWatchStateLookup;
    isWatchStateLoading: boolean;
    getWatchState: (video: VideoCardData) => VideoWatchState | null;
    getWatchStateByIdentity: (videoId?: string | null, watchUrl?: string | null) => VideoWatchState | null;
    refreshWatchStates: () => Promise<void>;
}

interface WatchStateProviderProps {
    children: JSX.Element | JSX.Element[];
}

const defaultContext: WatchStateContextType = {
    watchStateLookup: new Map(),
    isWatchStateLoading: false,
    getWatchState: () => null,
    getWatchStateByIdentity: () => null,
    refreshWatchStates: async () => undefined
};

const WatchStateContext: Context<WatchStateContextType> = createContext<WatchStateContextType>(defaultContext);

const WATCH_STATE_PAGE_SIZE = 250;

/**
 * Provider for signed-in video watch state used by reusable card surfaces.
 */
const WatchStateProvider = ({ children }: WatchStateProviderProps) => {
    const { isLoggedIn, isUserStateLoading, getUserInfo } = useUserState();
    const [watchStateLookup, setWatchStateLookup] = useState<VideoWatchStateLookup>(new Map());
    const [isWatchStateLoading, setIsWatchStateLoading] = useState(false);
    const isMountedRef = useRef(true);

    const loadWatchStates = useCallback(async () => {
        const userInfo = getUserInfo();
        if (!isLoggedIn || !userInfo?.token) {
            if (isMountedRef.current) {
                setWatchStateLookup(new Map());
                setIsWatchStateLoading(false);
            }
            return;
        }

        if (isMountedRef.current) {
            setIsWatchStateLoading(true);
        }

        try {
            const response = await fetchViewingHistory(userInfo.token, 1, WATCH_STATE_PAGE_SIZE);
            if (isMountedRef.current) {
                setWatchStateLookup(buildVideoWatchStateLookup(response.Results ?? []));
            }
        } catch (error) {
            if (isMountedRef.current) {
                setWatchStateLookup(new Map());
            }
        } finally {
            if (isMountedRef.current) {
                setIsWatchStateLoading(false);
            }
        }
    }, [getUserInfo, isLoggedIn]);

    useEffect(() => {
        isMountedRef.current = true;

        const syncWatchStates = async () => {
            if (isUserStateLoading) {
                return;
            }

            await loadWatchStates();
        };

        syncWatchStates();

        return () => {
            isMountedRef.current = false;
        };
    }, [isUserStateLoading, loadWatchStates]);

    const getWatchState = useCallback((video: VideoCardData) => (
        getVideoWatchStateForCard(watchStateLookup, video)
    ), [watchStateLookup]);

    const getWatchStateByIdentity = useCallback((videoId?: string | null, watchUrl?: string | null) => (
        getVideoWatchStateForIdentity(watchStateLookup, videoId, watchUrl)
    ), [watchStateLookup]);

    const value = useMemo(() => ({
        watchStateLookup,
        isWatchStateLoading,
        getWatchState,
        getWatchStateByIdentity,
        refreshWatchStates: loadWatchStates
    }), [getWatchState, getWatchStateByIdentity, isWatchStateLoading, loadWatchStates, watchStateLookup]);

    return (
        <WatchStateContext.Provider value={value}>
            {children}
        </WatchStateContext.Provider>
    );
};

/**
 * Access signed-in watch state for cards.
 */
export const useWatchState = () => {
    return useContext(WatchStateContext);
};

export default WatchStateProvider;
